import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, BadgeProps, IconButton, styled } from '@mui/material';
import { Routes } from 'enums';
import { isCoPilotPath } from 'features/co-pilot/co-pilot-routes';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { To, useLocation, useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import { makeStyles } from 'tss-react/mui';

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
  },
}));

interface Props {
  navigationState?: NavigationStates;
  route?: Routes;
  taskCount?: number;
  active?: boolean;
}

interface LocationState {
  from?: string;
  query?: string;
}

const TaskManagerNavigation: React.FC<Props> = ({ navigationState, route, taskCount, active }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as LocationState | null;
  const redirectPath = locationState?.from && locationState?.query ? `${locationState.from}${locationState.query}` : route;

  const { appBarNavigation, setAppBarNavigation } = useAppBarNavigationStore((state) => ({
    appBarNavigation: state.activeTab,
    setAppBarNavigation: state.setActiveTab,
  }));

  const isActive = !_.isNil(active) ? active : !_.isNil(route) ? location.pathname === route : appBarNavigation === navigationState;

  const handleItemClick = () => {
    if (!_.isNil(route)) {
      navigate(redirectPath ?? (-1 as To));
      if (!_.isNil(navigationState)) {
        setAppBarNavigation(navigationState, !isCoPilotPath(route));
      }
    } else {
      if (!_.isNil(navigationState)) {
        setAppBarNavigation(navigationState);
      }
    }
  };

  return (
    <PaiTooltip title={t('taskManager')} placement={'bottom'}>
      <IconButton className={cx(classes.navItem, isActive ? 'active' : null)} size={'small'} onClick={handleItemClick}>
        <StyledBadge badgeContent={taskCount} overlap={'rectangular'} color={'error'}>
          <NotificationsIcon />
        </StyledBadge>
      </IconButton>
    </PaiTooltip>
  );
};

const useStyles = makeStyles()((theme) => ({
  navItem: {
    color: '#000',
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&.active': {
      color: theme.palette.primary.main,
    },
  },
  navItemText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
    textTransform: 'uppercase',
    lineHeight: 1.4,
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(11),
    },
  },
}));

export default TaskManagerNavigation;
